import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Lang } from '../types';
interface FinalCTAProps {
  lang: Lang;
}
const content = {
  en: {
    headline: 'Your next tattoo should mean something.',
    subtext: 'Clean lines. Custom design. Professional studio. Miami.',
    body: "<strong class='text-studio-text'>Barboxa Ink at Piajas Ink Tattoo Studio is ready for you.</strong><br/>Don't settle for generic. Book your fine line session today.",
    btn: 'BOOK MY SESSION NOW',
    studio: 'Piajas Ink Tattoo Studio',
    location: '📍 2116 SW 8th St, Miami FL 33135',
    footer: 'Appointment only · Limited spots · Replies within 24–48h'
  },
  es: {
    headline: 'Tu próximo tatuaje debe significar algo.',
    subtext: 'Líneas limpias. Diseño personalizado. Estudio profesional. Miami.',
    body: "<strong class='text-studio-text'>Barboxa Ink en Piajas Ink Tattoo Studio está listo para ti.</strong><br/>No te conformes con lo genérico. Reserva tu sesión Fine Line hoy.",
    btn: 'RESERVAR MI SESIÓN AHORA',
    studio: 'Piajas Ink Tattoo Studio',
    location: '📍 2116 SW 8th St, Miami FL 33135',
    footer: 'Solo con cita · Cupos limitados · Respuesta en 24–48h'
  }
};
export function FinalCTA({
  lang
}: FinalCTAProps) {
  const t = content[lang];
  return <section className="py-16 md:py-20 bg-studio-bg relative overflow-hidden" data-id="element-94">
      {/* Decorative subtle lines */}
      <div className="absolute inset-0 pointer-events-none opacity-20" data-id="element-95">
        <div className="absolute top-1/2 left-0 w-full h-px bg-studio-accent transform -translate-y-1/2 rotate-12" data-id="element-96" />
        <div className="absolute top-1/2 left-0 w-full h-px bg-studio-accent transform -translate-y-1/2 -rotate-12" data-id="element-97" />
      </div>

      <div className="max-w-4xl mx-auto px-6 text-center relative z-10" data-id="element-98">
        <motion.h2 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="text-4xl md:text-6xl font-bold mb-6 text-balance" data-id="element-99">
          {t.headline}
        </motion.h2>

        <motion.h3 initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.1
      }} className="text-xl md:text-2xl font-playfair text-studio-accent italic mb-8" data-id="element-100">
          {t.subtext}
        </motion.h3>

        <motion.p initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2
      }} className="text-lg text-studio-muted mb-12 leading-relaxed" dangerouslySetInnerHTML={{
        __html: t.body
      }} data-id="element-101" />

        <motion.div initial={{
        opacity: 0,
        scale: 0.95
      }} whileInView={{
        opacity: 1,
        scale: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.3
      }} className="inline-block" data-id="element-102">
          <a href="#booking" className="group relative inline-flex flex-col items-center justify-center px-10 py-6 bg-studio-accent text-studio-bg font-semibold tracking-wide uppercase overflow-hidden transition-transform hover:scale-[1.02] active:scale-[0.98] mb-8" data-id="element-103">
            <span className="relative z-10 flex items-center gap-2 text-lg mb-2" data-id="element-104">
              📅 {t.btn}
            </span>
            <span className="relative z-10 text-sm font-normal opacity-90" data-id="element-105">
              {t.studio}
            </span>
            <span className="relative z-10 text-xs font-normal opacity-80 mt-1" data-id="element-106">
              {t.location}
            </span>
            <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" data-id="element-107" />
          </a>

          <p className="text-sm text-studio-muted italic" data-id="element-108">*{t.footer}</p>
        </motion.div>

        {/* Artist Portrait & Personal Message */}
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.2,
        duration: 0.8
      }} className="mt-24 pt-16 border-t border-studio-border" data-id="element-109">
          <div className="relative w-56 h-56 md:w-72 md:h-72 mx-auto mb-8 rounded-full overflow-hidden border-2 border-studio-accent/40 shadow-[0_0_40px_8px_rgba(201,169,110,0.15)]" data-id="element-110">
            <img src="https://cdn.magicpatterns.com/uploads/5WesX9wn34y7CaDLaBAXhd/image_Tatto_Artist.jpg" alt="Julián Ospina — Barboxa Ink" className="w-full h-full object-cover object-top" style={{
            filter: 'brightness(1.25) contrast(1.05) saturate(1.1)'
          }} data-id="element-111" />
          </div>
          <blockquote className="max-w-xl mx-auto" data-id="element-112">
            <p className="font-playfair italic text-xl md:text-2xl text-studio-text leading-relaxed" data-id="element-113">
              "Parce, trabajé de mi mano y te entregaré mi mejor dedicación en
              tu producto."
            </p>
            <footer className="mt-4 text-studio-accent font-medium tracking-wide" data-id="element-114">
              — Julián Ospina, Barboxa Ink 🇨🇴
            </footer>
          </blockquote>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10" data-id="element-115">
            <a href="tel:+17867620617" className="group relative inline-flex items-center justify-center gap-3 w-full sm:w-auto px-8 py-4 bg-studio-accent text-studio-bg font-semibold tracking-wide uppercase overflow-hidden transition-transform hover:scale-[1.02] active:scale-[0.98]" data-id="element-116">
              <span className="relative z-10 flex items-center gap-2" data-id="element-117">
                📞 {lang === 'en' ? 'Call Now' : 'Llamar Ahora'}
              </span>
              <span className="relative z-10 text-sm font-normal opacity-90" data-id="element-118">
                786-762-0617
              </span>
              <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-300 ease-out" data-id="element-119" />
            </a>

            <a href="https://www.google.com/maps/dir/?api=1&destination=2116+SW+8th+St,+Miami+FL+33135" target="_blank" rel="noopener noreferrer" className="group relative inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 border-2 border-studio-accent text-studio-accent font-semibold tracking-wide uppercase overflow-hidden transition-all hover:bg-studio-accent hover:text-studio-bg hover:scale-[1.02] active:scale-[0.98]" data-id="element-120">
              <span className="relative z-10 flex items-center gap-2" data-id="element-121">
                📍 {lang === 'en' ? 'Get Directions' : 'Cómo Llegar'}
              </span>
            </a>
          </div>

          {/* Embedded Map */}
          <div className="mt-12 w-full max-w-2xl mx-auto rounded-lg overflow-hidden border border-studio-border shadow-lg" data-id="element-122">
            <iframe title="Piajas Ink Tattoo Studio — 2116 SW 8th St, Miami FL 33135" src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3593.1!2d-80.2225!3d25.7653!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x88d9b6843a26c7f1%3A0x0!2s2116+SW+8th+St%2C+Miami%2C+FL+33135!5e0!3m2!1sen!2sus!4v1700000000000" width="100%" height="300" style={{
            border: 0,
            filter: 'invert(0.9) hue-rotate(180deg) brightness(0.95) contrast(1.1)'
          }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" data-id="element-123" />
            <div className="bg-studio-bgAlt px-6 py-4 flex items-center justify-between" data-id="element-124">
              <div data-id="element-125">
                <p className="text-studio-text font-semibold text-sm" data-id="element-126">
                  Piajas Ink Tattoo Studio
                </p>
                <p className="text-studio-muted text-xs mt-0.5" data-id="element-127">
                  2116 SW 8th St, Miami FL 33135
                </p>
              </div>
              <a href="https://www.google.com/maps/dir/?api=1&destination=2116+SW+8th+St,+Miami+FL+33135" target="_blank" rel="noopener noreferrer" className="text-xs text-studio-accent font-medium uppercase tracking-wider hover:underline" data-id="element-128">
                {lang === 'en' ? 'Open in Maps →' : 'Abrir en Maps →'}
              </a>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}